import { useNavigate } from 'react-router-dom';
import WrapInputLabel from 'common/components/wrap-input-label';
import { Form, Formik } from 'formik';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import ApplyButtons from 'components/apply-shared/apply-buttons';
import {
  useCreatePlanMutation,
  useUpdateSinglePlanMutation,
} from 'redux/api/ciosUsersApi/ciosUsersApi';
import {
  saveCoverageSelection,
  savePlansValues,
  saveUpfrontSplitAmount,
} from 'redux/features/plans/plansSlice';
import { useDispatch } from 'react-redux';
import { isEmpty } from 'common/utils/isEmpty';
import useUserTracker from 'common/utils/user-tracker/useUserTracker';
import { extractErrorMsg } from 'utils/errors';
import {
  ApplyFormContainer,
  ApplyFormField,
  ApplyFormLayout,
} from 'common/layouts/apply-form';
import { Flex, Grid, Icon, Text } from '@chakra-ui/react';
import ContactCard from 'components/shared/contact-card';
import ApplyTitle from 'components/apply-shared/apply-title';
import ApplyContent from 'components/apply-shared/apply-content';
import Input from 'common/components/input';
import FormError from 'common/components/form-error';
import { planStatusEnum } from 'common/utils/enums';
import { toDatePickerFormat } from 'common/utils/toDatePickerFormat';
import { FaHouseCircleCheck } from 'react-icons/fa6';
import { FaSearchLocation } from 'react-icons/fa';
import PlanIneligibleModal from 'components/plans/plan-ineligible-modal';
import RadioCard from 'components/shared/radio-card';

const DatesQuote = ({ planId, planData, formikRef, onNextClick, ready }) => {
  const navigate = useNavigate();

  const [hasSignedLease, setHasSignedLease] = useState(null);
  const isDateSet =
    !isEmpty(planData?.lease?.signDate) ||
    !isEmpty(planData?.preLeaseExpiration);
  const [modalPlanId, setModalPlanId] = useState(null);

  const [isInvalidDateModalOpen, setInvalidDateModalOpen] = useState(false);
  useEffect(() => {
    if (!isEmpty(planData?.lease?.signDate)) {
      setHasSignedLease(true);
    } else if (!isEmpty(planData?.preLeaseExpiration)) {
      setHasSignedLease(false);
    }
  }, [planData]);

  const dispatch = useDispatch();
  // API mutations
  const [
    createAPlan,
    { isLoading: createPlanLoading, isSuccess: isCreatePlanSuccess },
  ] = useCreatePlanMutation();
  const [
    updatePlan,
    { isLoading: updatePlanLoading, isSuccess: isUpdatePlanSuccess },
  ] = useUpdateSinglePlanMutation();

  // SIDE EFFECTS
  // User tracking
  const {
    trackFormSubmission,
    trackFormSubmissionSuccess,
    trackFormSubmissionError,
    trackCustomEvent,
    trackPageLoad,
  } = useUserTracker();

  useEffect(() => {
    trackPageLoad('Select Dates for Quote');
  }, []);

  const sevenDaysAgo = useMemo(() => {
    return moment().subtract(7, 'days').startOf('day').toDate();
  }, []);

  return (
    <>
      <Formik
        innerRef={formikRef}
        enableReinitialize
        initialValues={{
          preLeaseExpiration:
            toDatePickerFormat(planData?.preLeaseExpiration) || '',
          signDate: toDatePickerFormat(planData?.lease?.signDate) || '',
          term: planData?.lease?.term || 12,
        }}
        validationSchema={Yup.object().shape({
          term: Yup.number().required('Please select a lease term'),
          preLeaseExpiration:
            hasSignedLease === false
              ? Yup.date().required('Please select when your current lease expires')
              : Yup.date().nullable(),
          signDate:
            hasSignedLease === true
              ? Yup.date()
                  .required('Please select the date you signed your lease')
                  .max(
                    moment().endOf('day').toDate(),
                    'Your lease sign date cannot be in the future'
                  )
              : Yup.date().nullable(),
        })}
        onSubmit={(values) => {
          const isDateInvalid =
            hasSignedLease === true &&
            moment(values.signDate).isBefore(sevenDaysAgo);

          let submissionValues = {
            status: planStatusEnum.QUOTE,
            preLeaseExpiration: values?.preLeaseExpiration,
            lease: {
              ...planData?.lease,
              signDate: values?.signDate,
              term: values?.term,
            },
          };

          if (hasSignedLease === true) {
            delete submissionValues.preLeaseExpiration;
          } else if (hasSignedLease === false) {
            delete submissionValues.lease.signDate;
          }

          let mutation = createAPlan;
          if (planId) {
            submissionValues.planId = planId;
            mutation = updatePlan;
          }

          trackFormSubmission(submissionValues);

          if (isDateInvalid) {
            submissionValues.status = planStatusEnum.NOT_ELIGIBLE;
            mutation(submissionValues)
              .unwrap()
              .then((data) => {
                // Clear redux state after successful submission
                dispatch(saveCoverageSelection({}));
                dispatch(saveUpfrontSplitAmount(null));
                dispatch(savePlansValues({}));
                trackFormSubmissionSuccess(submissionValues);
                setModalPlanId(data?.id);
                setInvalidDateModalOpen(true);
              })
              .catch((error) => {
                // Changed error message to be more generic as update is disabled
                const errMsg = extractErrorMsg(
                  error,
                  'We were unable to create your quote. Please reload the page and try again.'
                );
                toast.error(errMsg);
                trackFormSubmissionError(submissionValues, errMsg);
              });
          } else {
            mutation(submissionValues)
              .unwrap()
              .then((data) => {
                // Clear redux state after successful submission
                dispatch(saveCoverageSelection({}));
                dispatch(saveUpfrontSplitAmount(null));
                dispatch(savePlansValues({}));
                trackFormSubmissionSuccess(submissionValues);
                onNextClick(data?.id);
              })
              .catch((error) => {
                // Changed error message to be more generic as update is disabled
                const errMsg = extractErrorMsg(
                  error,
                  'We were unable to create your quote. Please reload the page and try again.'
                );
                toast.error(errMsg);
                trackFormSubmissionError(submissionValues, errMsg);
              });
          }
        }}
      >
        {({
          handleBlur,
          handleChange,
          setFieldValue,
          values,
          isValid,
          dirty,
          handleSubmit,
          setFieldTouched,
          errors,
          touched,
          submitCount,
        }) => {
          return (
            <Form className='flex flex-col w-full h-full'>
              <ApplyTitle
                title='Get a ReLease Quote'
                subtitle='Have you already signed a lease?'
                buttons={
                  <ApplyButtons
                    loading={createPlanLoading /* || updatePlanLoading */}
                    isValid={isValid && hasSignedLease !== null}
                    moveOn={!dirty && hasSignedLease !== null}
                    handleSubmit={handleSubmit}
                    onNextClick={onNextClick}
                    onBackClick={() => {
                      window.location.href = '/dashboard/plans/quote';
                    }}
                    disableBack={!isDateSet}
                    backTitle='Start a New Quote'
                    nextTitle={'Add Lease Details'}
                  />
                }
              />

              {/* main content */}
              <ApplyContent className='h-full justify-between'>
                {/* container */}
                <ApplyFormContainer>
                  <Grid className='grid-cols-1 md:grid-cols-2 gap-6 mb-6'>
                    <RadioCard
                      isSelected={hasSignedLease === true}
                      isDisabled={isDateSet}
                      onClick={() => {
                        if (isDateSet) return;
                        setHasSignedLease(true);
                        setFieldValue('preLeaseExpiration', '');
                      }}
                      icon={FaHouseCircleCheck}
                      label="Yes, I've already signed a lease"
                      description={
                        <span>
                          Whether you’re moving or renewing, tell us when you
                          signed your lease agreement. You’ll need to purchase coverage within 7 days
                          of signing.
                        </span>
                      }
                    />
                    <RadioCard
                      isSelected={hasSignedLease === false}
                      isDisabled={isDateSet}
                      onClick={() => {
                        if (isDateSet) return;
                        setHasSignedLease(false);
                        setFieldValue('signDate', '');
                      }}
                      icon={FaSearchLocation}
                      label="No, I haven't signed a lease yet"
                      description={
                        <span>
                          No problem—just provide the end date of your
                          current lease. You can create as many quotes as you’d
                          like, for all the places you’re considering.
                        </span>
                      }
                    />
                  </Grid>

                  {hasSignedLease !== null && (
                    <ApplyFormLayout className='pt-8'>
                      {hasSignedLease === true && (
                        <>
                          {/* Lease Signing Date */}
                          <ApplyFormField>
                            <WrapInputLabel isRequired>
                              When did you sign your lease agreement?
                            </WrapInputLabel>
                            <Input
                              type='date'
                              value={values.signDate}
                              onChange={(event) => {
                                handleChange(event);
                                setFieldTouched('signDate');
                              }}
                              onBlur={handleBlur}
                              disabled={isDateSet}
                              name='signDate'
                              max={toDatePickerFormat(moment().toDate())}
                            />
                            <Text className='text-sm text-gray-500 ml-1'>
                              To be eligible, you must purchase ReLease within 7 days of your lease signing date.
                            </Text>
                            <FormError name='signDate' />
                          </ApplyFormField>
                        </>
                      )}

                      {hasSignedLease === false && (
                        <>
                          {/* Previous Lease End Date */}
                          <ApplyFormField>
                            <WrapInputLabel isRequired>
                              When does your current lease end?
                            </WrapInputLabel>
                            <Input
                              type='date'
                              value={values.preLeaseExpiration}
                              onChange={(event) => {
                                handleChange(event);
                                setFieldTouched('preLeaseExpiration');
                              }}
                              onBlur={handleBlur}
                              disabled={isDateSet}
                              name='preLeaseExpiration'
                              min={toDatePickerFormat(
                                moment().subtract(7, 'days')
                              )}
                            />
                            <Text className='text-sm text-gray-500 ml-1'>
                              If you don't have a current lease, enter your
                              estimated move-in date.
                            </Text>
                            <FormError name='preLeaseExpiration' />
                          </ApplyFormField>
                        </>
                      )}

                      {hasSignedLease !== null && (
                        <>
                          {/* Term */}
                          <ApplyFormField>
                            <WrapInputLabel isRequired>
                              {hasSignedLease
                                ? 'How long is your lease length?'
                                : 'What lease length are you looking to sign?'}
                            </WrapInputLabel>
                            <Input
                              type='number'
                              value={values.term}
                              onChange={(event) => {
                                handleChange(event);
                                setFieldTouched('term');
                              }}
                              onBlur={handleBlur}
                              name='term'
                              min={6}
                              max={24}
                              suffix='months'
                            />
                            <Text className='text-sm text-gray-500 ml-1'>
                              ReLease is available for leases between 6 and 24
                              months in length.
                            </Text>
                            <FormError name='term' />
                          </ApplyFormField>
                        </>
                      )}
                    </ApplyFormLayout>
                  )}
                </ApplyFormContainer>
                <ContactCard
                  border={false}
                  className='pt-4 border-t'
                  title='Need help with your quote?'
                  subtitle='Contact us via email or phone to get help with your quote'
                />
              </ApplyContent>
            </Form>
          );
        }}
      </Formik>
      {isInvalidDateModalOpen && (
        <PlanIneligibleModal
          isOpen={isInvalidDateModalOpen}
          onClose={() => {
            setInvalidDateModalOpen(false);
            navigate('/dashboard/plans');
          }}
          planId={modalPlanId}
        />
      )}
    </>
  );
};

export default DatesQuote;
